import type { ExternalBooking } from 'patient-api-lib'
import { type FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import ClinicIcon from '@/assets/icons/clinic-icon.svg?react'
import DoctorIcon from '@/assets/icons/doctor-user-icon.svg?react'
import TreatmentIcon from '@/assets/icons/treatment-icon.svg?react'
import { Button } from '@/components/Button/Button'
import { capitalizeFirstLetter } from '@/helpers/capitalizeFirstLetter'
import { RouterPath } from '@/models/enums'
import { formatDate } from '@/utils/dateFormatters'

import Loading from './Loading'

type BookingCardProps = {
  booking: ExternalBooking.AsObject | null
  loading?: boolean
  className?: string
  error?: boolean
  onRetry?: () => void
}

const BookingCard: FC<BookingCardProps> = (props) => {
  const { booking, className = '', loading, error = false, onRetry } = props
  const { t } = useTranslation('booking-card')
  const navigate = useNavigate()
  const handleClick = () => {
    navigate(`/${RouterPath.Hub}/${RouterPath.MakeAppointment}`)
  }

  if (loading) {
    return <Loading />
  }

  if (error) {
    return (
      <div className="my-auto text-center">
        <p>{t('error')}</p>
        <Button className="mx-auto mt-4 h-auto w-[114px] uppercase" onClick={onRetry}>
          {t('error_button')}
        </Button>
      </div>
    )
  }

  if (!booking) {
    return (
      <div className="my-auto text-left">
        <p className="mb-4">{t('description')}</p>
        <p>{t('description_2')}</p>
        <div className="mt-4">
          <Button className="ml-auto h-auto w-[114px] uppercase" onClick={handleClick}>
            {t('action_button')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {booking?.startTime && (
        <h3 className="mb-5 mt-1 font-satoshi text-lg font-bold leading-none">
          {capitalizeFirstLetter(formatDate(booking.startTime, 'EEEE'))} {formatDate(booking.startTime, 'd MMM')} ·{' '}
          {formatDate(booking.startTime, 'HH:mm')}
        </h3>
      )}
      <ul className="space-y-2">
        {booking?.clinicName?.value && (
          <li className="flex items-center gap-2 font-satoshi text-sm font-bold leading-5">
            <ClinicIcon width={14} className="shrink-0" fill="#0a293a" /> {booking?.clinicName?.value}
          </li>
        )}
        {booking?.treatmentName?.value && (
          <li className="flex items-center gap-2 font-satoshi text-sm font-bold leading-5">
            <TreatmentIcon width={14} className="shrink-0" fill="#0a293a" /> {booking?.treatmentName?.value}
          </li>
        )}
        {booking?.caregiverName?.value && (
          <li className="flex items-center gap-2 font-satoshi text-sm font-bold leading-5">
            <DoctorIcon width={14} className="shrink-0" fill="#0a293a" /> {booking?.caregiverName?.value}
          </li>
        )}
      </ul>
    </div>
  )
}

export default BookingCard
